import { supabase } from '@/integrations/supabase/client';

const ACTIVE_SESSION_KEY = 'active_tracking_session_id';
const ACTIVE_USER_KEY = 'active_tracking_user_id';
const RECENT_LOGIN_KEY = 'recent_login_tracking_event';
const LOGIN_DEDUPE_WINDOW_MS = 10_000;

interface RecentLoginEvent {
  userId: string;
  trackedAt: number;
  sessionId?: string;
}

const getRecentLoginEvent = (): RecentLoginEvent | null => {
  try {
    const raw = sessionStorage.getItem(RECENT_LOGIN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setRecentLoginEvent = (event: RecentLoginEvent) => {
  try {
    sessionStorage.setItem(RECENT_LOGIN_KEY, JSON.stringify(event));
  } catch {
    // Browsers can block storage in some embedded contexts. Tracking should not block auth.
  }
};

export const recordLoginSession = async (
  userId: string,
  metadata: Record<string, unknown> = {}
) => {
  const now = Date.now();
  const recent = getRecentLoginEvent();

  if (
    recent?.userId === userId &&
    now - recent.trackedAt < LOGIN_DEDUPE_WINDOW_MS
  ) {
    return recent.sessionId || null;
  }

  setRecentLoginEvent({ userId, trackedAt: now });

  const { data, error } = await supabase
    .from('log_user_sessions' as any)
    .insert({
      user_id: userId,
      metadata: {
        source: 'web_app',
        ...metadata,
      },
    } as any)
    .select('id')
    .single();

  if (error) {
    throw error;
  }

  const sessionId = (data as { id?: string } | null)?.id || null;

  if (sessionId) {
    try {
      localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
      localStorage.setItem(ACTIVE_USER_KEY, userId);
      setRecentLoginEvent({ userId, trackedAt: now, sessionId });
    } catch {
      // Tracking storage is best-effort and must not interrupt login.
    }
  }

  return sessionId;
};

export const recordLogoutSession = async () => {
  let activeTrackingSessionId: string | null = null;

  try {
    activeTrackingSessionId = localStorage.getItem(ACTIVE_SESSION_KEY);
  } catch {
    activeTrackingSessionId = null;
  }

  if (!activeTrackingSessionId) return;

  const { error } = await supabase
    .from('log_user_sessions' as any)
    .update({ logout_at: new Date().toISOString() } as any)
    .eq('id', activeTrackingSessionId);

  if (error) {
    throw error;
  }

  try {
    localStorage.removeItem(ACTIVE_SESSION_KEY);
    localStorage.removeItem(ACTIVE_USER_KEY);
  } catch {
    // Storage cleanup is best-effort.
  }
};
